import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Spinner } from "./components/Spinner";

// Eagerly imported: Login + Dashboard. Login is the unauth landing for
// /login, Dashboard is the post-auth landing for /. Both are tiny and
// almost everyone hits at least one, so we keep them in the main bundle.
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";

// Heavyweight pages — lazy-loaded so the initial bundle doesn't ship
// Stripe Elements, big forms, and per-page state for routes the user may
// never visit. Each lazy() boundary becomes its own JS chunk that
// downloads on first navigation to that route.
const Billing = lazy(() => import("./pages/Billing"));
const Services = lazy(() => import("./pages/Services"));
const PurchaseServices = lazy(() => import("./pages/PurchaseServices"));
const Support = lazy(() => import("./pages/Support"));
const Projects = lazy(() => import("./pages/Projects"));
const Profile = lazy(() => import("./pages/Profile"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Signup = lazy(() => import("./pages/Signup"));
const DomainsEmail = lazy(() => import("./pages/DomainsEmail"));

// Admin pages — lazy-loaded so clients (the majority of traffic) don't pay
// for admin code in their initial bundle. Each lazy() boundary becomes a
// separate JS chunk that downloads on first navigation to that route.
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminClients = lazy(() =>
  import("./pages/admin/AdminClients").then((m) => ({ default: m.AdminClients }))
);
const AdminNewClient = lazy(() =>
  import("./pages/admin/AdminClients").then((m) => ({ default: m.AdminNewClient }))
);
const AdminEditClient = lazy(() =>
  import("./pages/admin/AdminClients").then((m) => ({ default: m.AdminEditClient }))
);
const AdminClientDetail = lazy(() =>
  import("./pages/admin/AdminClientDetail").then((m) => ({
    default: m.AdminClientDetail,
  }))
);
const AdminServices = lazy(() =>
  import("./pages/admin/AdminServices").then((m) => ({ default: m.AdminServices }))
);
const AdminEditService = lazy(() =>
  import("./pages/admin/AdminServices").then((m) => ({
    default: m.AdminEditService,
  }))
);
const AdminSupport = lazy(() =>
  import("./pages/admin/AdminSupport").then((m) => ({ default: m.AdminSupport }))
);
const AdminProjects = lazy(() =>
  import("./pages/admin/AdminProjects").then((m) => ({ default: m.AdminProjects }))
);
const AdminEmails = lazy(() =>
  import("./pages/admin/AdminEmails").then((m) => ({ default: m.AdminEmails }))
);
const AdminEditEmail = lazy(() =>
  import("./pages/admin/AdminEmails").then((m) => ({ default: m.AdminEditEmail }))
);
const AdminInvoice = lazy(() =>
  import("./pages/admin/AdminInvoice").then((m) => ({ default: m.AdminInvoice }))
);
const AdminImportStripe = lazy(() =>
  import("./pages/admin/AdminImportStripe").then((m) => ({
    default: m.AdminImportStripe,
  }))
);
const AdminDomainRequests = lazy(() =>
  import("./pages/admin/AdminDomainRequests").then((m) => ({
    default: m.AdminDomainRequests,
  }))
);

/**
 * Branded loader rendered while a lazy chunk downloads. The Spinner adds a
 * subtle pulse on the ProNerdz logo so the user knows the app is alive on
 * slower connections without being distracting on fast ones.
 */
function RouteFallback() {
  return <Spinner size="lg" fullScreen label="Loading" />;
}

export default function App() {
  return (
    <AuthProvider>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          {/* Public shop — visitors browse without signing in. The "Buy"
              button on each card routes signed-in clients to the existing
              /services/:id/checkout flow and visitors to /start/:id. */}
          <Route path="/shop" element={<PurchaseServices />} />
          {/* Public signup-and-pay flow. Lives outside ProtectedRoute so
              visitors can land here from a marketing-site CTA. */}
          <Route path="/start/:serviceId" element={<Signup />} />

          {/* Client portal */}
          <Route
            path="/"
            element={
              <ProtectedRoute require="client">
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute require="client">
                <Billing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/services"
            element={
              <ProtectedRoute require="client">
                <Services />
              </ProtectedRoute>
            }
          />
          <Route
            path="/services/purchase"
            element={
              <ProtectedRoute require="client">
                <PurchaseServices />
              </ProtectedRoute>
            }
          />
          <Route
            path="/services/:serviceId/checkout"
            element={
              <ProtectedRoute require="client">
                <Checkout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/domains"
            element={
              <ProtectedRoute require="client">
                <DomainsEmail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support"
            element={
              <ProtectedRoute require="client">
                <Support />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute require="client">
                <Projects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute require="client">
                <Profile />
              </ProtectedRoute>
            }
          />

          {/* Admin portal — lazy-loaded chunks */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute require="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clients"
            element={
              <ProtectedRoute require="admin">
                <AdminClients />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clients/new"
            element={
              <ProtectedRoute require="admin">
                <AdminNewClient />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clients/import"
            element={
              <ProtectedRoute require="admin">
                <AdminImportStripe />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clients/:clientId/invoice"
            element={
              <ProtectedRoute require="admin">
                <AdminInvoice />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clients/:clientId/edit"
            element={
              <ProtectedRoute require="admin">
                <AdminEditClient />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clients/:clientId"
            element={
              <ProtectedRoute require="admin">
                <AdminClientDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/services"
            element={
              <ProtectedRoute require="admin">
                <AdminServices />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/services/new"
            element={
              <ProtectedRoute require="admin">
                <AdminEditService />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/services/:serviceId/edit"
            element={
              <ProtectedRoute require="admin">
                <AdminEditService />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/support"
            element={
              <ProtectedRoute require="admin">
                <AdminSupport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/projects"
            element={
              <ProtectedRoute require="admin">
                <AdminProjects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/domain-requests"
            element={
              <ProtectedRoute require="admin">
                <AdminDomainRequests />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/emails"
            element={
              <ProtectedRoute require="admin">
                <AdminEmails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/emails/:templateId"
            element={
              <ProtectedRoute require="admin">
                <AdminEditEmail />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}
